import React, { useCallback, useMemo, useRef, useState } from "react";
import DocumentAddedIcon from "../../../../../../../../../assets/svg/document-added-icon.tsx";
import { getFileTypeIcon } from "../../../../../../../../../utils/functions/file-icon-extractor.ts";
import AddIcon from "../../../../../../../../../assets/svg/add-icon.tsx";
import Button from "../../../../../../../../../components/Button/Button.tsx";
import CloseIcon from "../../../../../../../../../assets/svg/close-icon.tsx";
import SearchableDropdown from "../../../../../../../../../components/SearchableDropdown/SearchableDropdown.tsx";
import { customToast } from "../../../../../../../../../utils/custom-toast/custom-toast.tsx";
import CompletedIcon from "../../../../../../../../../assets/svg/completed-icon.tsx";
import { useUploadKnowledgeBaseDocumentsMutation } from "../../../../../../../../../redux/services/helpdeskApi.ts";
import { Tooltip } from "../../../../../../../../../components/Tooltip/Tooltip.tsx";
import CheckIcon from "../../../../../../../../../assets/svg/check-icon.tsx";
import AlertIcon from "../../../../../../../../../assets/svg/alert-icon.tsx";
import ClipLoader from "../../../../../../../../../components/Common/ClipLoader.tsx";
import { trackEvents } from "../../../../../../../../../utils/amplitude/amplitude";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../../redux/store";
import Dropdownv2 from "../../../../../../../../../components/Dropdown/Dropdownv2.tsx";

export interface CategoryOption {
  id: string;
  title: string;
  icon: React.FC<{ className?: string }> | string;
  iconClassName: string;
}

const UploadDocuments = ({
  categories,
  onClose,
  setScrollToSection,
  defaultCategoryId,
  disableCategorySelection = false,
}: {
  categories: CategoryOption[];
  onClose: () => void;
  setScrollToSection?: (scrollToSection: string | null) => void;
  defaultCategoryId?: string;
  disableCategorySelection?: boolean;
}) => {
  const orgId =
    useSelector((state: RootState) => state?.auth?.client?.client_code) ||
    "unknown";
  const userId =
    useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";
  const [file, setFile] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileSystemNames, setFileSystemNames] = useState<
    Map<string, { id: string; name: string }>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const shouldEnableListScroll = file?.length > 5;
  const listContainerClassName = `flex flex-col gap-3 slim-scrollbar ${
    shouldEnableListScroll
      ? "overflow-y-auto max-h-[280px]"
      : "overflow-y-visible"
  }`;
  const maxSize = "225mb";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [uploadKnowledgeBaseDocuments] =
    useUploadKnowledgeBaseDocumentsMutation();

  const renderCategoryIcon = (cat: CategoryOption, className: string) => {
    if (typeof cat?.icon === "string") {
      return <img src={cat?.icon} alt={cat?.title} className={className} />;
    }
    const IconComponent = cat?.icon;
    return <IconComponent className={className} />;
  };

  const systemNameOptions = useMemo(() => {
    return categories?.map((cat) => ({
      value: cat?.id,
      label: cat?.title,
      content: (
        <span className="flex items-center gap-2 text-sm text-heading-text dark:text-heading-text-dk">
          {renderCategoryIcon(
            cat,
            "w-4 h-4 object-contain shrink-0 text-verba-blue dark:text-verba-blue-dk"
          )}
          {cat?.title}
        </span>
      ),
      selectedOptionContent: (
        <span className="flex items-center gap-2 text-sm text-heading-text dark:text-heading-text-dk">
          {renderCategoryIcon(
            cat,
            "w-4 h-4 object-contain shrink-0 text-verba-blue dark:text-verba-blue-dk"
          )}
          {cat?.title}
        </span>
      ),
    }));
  }, [categories]);

  const handleFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const validateFile = (file: File): boolean => {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        const sizeUnit = maxSize?.slice(-2)?.toLowerCase();
        const sizeValue = parseInt(maxSize?.slice(0, -2));
        const maxBytes =
          sizeUnit === "mb" ? sizeValue * 1024 * 1024 : sizeValue * 1024;

        if (!allowedTypes?.includes(file.type)) {
          customToast(
            "File type not supported. Please upload DOCX, PDF, or XLSX files.",
            "error",
            undefined,
            undefined,
            "bottom-left"
          );
          return false;
        }

        if (file?.size > maxBytes) {
          customToast(
            `File size exceeds ${maxSize} limit.`,
            "error",
            undefined,
            undefined,
            "bottom-left"
          );
          return false;
        }

        return true;
      };

      setFile((prevFiles) => {
        const existingFileNames = new Set(prevFiles?.map((f) => f.name));
        const validNewFiles = Array.from(newFiles)?.filter(
          (f) => validateFile(f) && !existingFileNames?.has(f.name)
        );

        if (validNewFiles?.length === 0) return prevFiles;

        const remainingSlots = 25 - prevFiles?.length;
        const filesToAdd = validNewFiles?.slice(0, remainingSlots);

        if (defaultCategoryId) {
          const defaultCat = categories?.find((c) => c?.id === defaultCategoryId);
          if (defaultCat) {
            setFileSystemNames((prev) => {
              const updated = new Map(prev);
              filesToAdd.forEach((f) => {
                updated.set(f.name, { id: defaultCat.id, name: defaultCat.title });
              });
              return updated;
            });
          }
        }

        return [...prevFiles, ...filesToAdd];
      });
    },
    [maxSize, defaultCategoryId, categories],
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsDragging(false);

      const droppedFiles = e?.dataTransfer?.files;
      handleFiles(droppedFiles);
    },
    [handleFiles]
  );

  const handleClick = () => {
    if (success) {
      setSuccess(false);
      setError(false);
      setIsLoading(false);
      setFile([]);
      setFileSystemNames(new Map());
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      handleFiles(e?.target?.files);
    }
  };

  const handleBulkUpload = useCallback(
    async (files: File[]) => {
      setIsLoading(true);
      try {
        const fileCategoryIds = files?.map((f) => {
          return fileSystemNames?.get(f?.name)?.id || "";
        });

        await uploadKnowledgeBaseDocuments({
          files,
          categories: fileCategoryIds,
        }).unwrap();

        files?.forEach((fileItem) => {
          const systemInfo = fileSystemNames?.get(fileItem?.name) || {
            id: "",
            name: "",
          };
          trackEvents("document_uploaded", {
            file_name: fileItem?.name,
            file_size: fileItem?.size,
            system_name: systemInfo?.name,
            status: "uploaded",
            uploaded_by: userId,
            org_id: orgId,
          });
        });

        setSuccess(true);
        return true;
      } catch (err) {
        console.error("Upload error:", err);
        setError(true);
        return false;
      } finally {
        setIsLoading(false);
        setScrollToSection?.("documents");
        onClose();
      }
    },
    [
      uploadKnowledgeBaseDocuments,
      fileSystemNames,
      userId,
      orgId,
      onClose,
      setScrollToSection,
    ]
  );

  const handleSystemNameChange = useCallback(
    (
      fileName: string,
      value: string,
      option: { label?: string; value: string }
    ) => {
      setFileSystemNames((prev) => {
        const updated = new Map(prev);
        const categoryName =
          option?.label ||
          categories?.find((c) => c?.id === value)?.title ||
          "";
        updated?.set(fileName, { id: value, name: categoryName });
        return updated;
      });
    },
    [categories]
  );

  const tableHeader = [
    {
      label: "Document",
      key: "document",
    },
    {
      label: "System Name",
      key: "systemName",
    },
    {
      label: "File Size",
      key: "fileSize",
    },
  ];

  return (
    <div className="flex flex-col gap-2.5 h-full overflow-y-auto slim-scrollbar">
      <DocumentAddedIcon className="w-11.5 h-11.5 text-verba-blue dark:text-verba-blue-dk shrink-0" />
      <h2 className="text-2xl font-medium text-verba-indigo dark:text-verba-indigo-dk">
        Upload Documents
      </h2>
      <p className="text-base text-verba-indigo dark:text-verba-indigo-dk">
        Upload your files and let VerbaFlo AI create fresh, insightful content
        instantly. Documents in{" "}
        <span className="font-semibold">.doc, .pdf or .xlsx</span> formats are
        supported
      </p>
      <div className="flex items-center gap-2">
        <img src={getFileTypeIcon("Doc")} alt="doc" className="w-6 h-6" />
        <img src={getFileTypeIcon("PDF")} alt="pdf" className="w-6 h-6" />
        <img src={getFileTypeIcon("xlsx")} alt="xlsx" className="w-6 h-6" />
      </div>

      <div
        ref={dropZoneRef}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border group hover:border-verba-blue-150 dark:hover:border-verba-blue-150-dk hover:border-dashed border-border-gray dark:border-border-gray-dk hover:bg-verba-blue-light-card-bg dark:hover:bg-verba-blue-light-card-bg-dk rounded-lg py-[26px] px-[18px] cursor-pointer mt-[14px] ${
          isDragging
            ? "border-verba-blue-150 dark:border-verba-blue-150-dk border-dashed bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
            : ""
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          multiple={true}
          disabled={file?.length >= 25}
        />
        <div className="flex items-center gap-2">
          <AddIcon className="w-6 h-6 text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk" />
          <div className="flex flex-col items-start">
            <div className="text-base text-verba-indigo dark:text-verba-indigo-dk font-medium group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk">
              Upload Document
            </div>
            <p className="text-sm text-dark-gray dark:text-dark-gray-dk flex gap-1">
              Select or Drag & Drop{" "}
              <span className="font-semibold">DOC, PDF,</span> and{" "}
              <span className="font-semibold">XLSX</span>
              files
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 mt-[14px]">
        <span className="text-base text-dark-gray dark:text-dark-gray-dk font-medium">
          Maximum size:
        </span>
        <span className="text-base text-heading-text dark:text-heading-text-dk font-medium">
          225MB
        </span>
        <div className="w-px h-[17px] bg-border-gray dark:bg-border-gray-dk mx-1"></div>
        <span className="text-base text-dark-gray dark:text-dark-gray-dk font-medium">
          Maximum number of files:
        </span>
        <span className="text-base text-heading-text dark:text-heading-text-dk font-medium">
          25
        </span>
      </div>

      {file && file?.length > 0 && (
        <div className="flex flex-col gap-3 p-4 border border-dashed border-verba-blue-150 dark:border-verba-blue-150-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk rounded-lg mb-6 max-h-[400px] mt-[12px]">
          <div className="flex items-center justify-between gap-2 border-b border-border-gray dark:border-border-gray-dk pb-2">
            <p className="text-base font-medium text-verba-indigo dark:text-verba-indigo-dk">
              Selected {file?.length} Documents
            </p>
          </div>
          <div className="grid grid-cols-[1fr_150px_auto_auto] gap-2 items-center">
            {tableHeader?.map((header) => (
              <span
                key={header.key}
                className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk"
              >
                {header.label}
              </span>
            ))}
          </div>
          <div className={listContainerClassName}>
            {file?.map((fileItem: File) => (
              <div
                key={fileItem.name}
                className="grid grid-cols-[1fr_150px_auto_auto] gap-2 items-center"
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={getFileTypeIcon(fileItem.name)}
                      alt="doc"
                      className="w-6 h-6 shrink-0"
                    />
                    <Tooltip content={fileItem.name}>
                      <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk font-medium truncate min-w-0">
                        {fileItem.name}
                      </span>
                    </Tooltip>
                  </div>
                </div>

                  <Dropdownv2
                    options={systemNameOptions}
                    value={fileSystemNames?.get(fileItem?.name)?.id || ""}
                    onChange={(value, option) =>
                      handleSystemNameChange(fileItem?.name, value, option)
                    }
                    className="w-[150px]"
                    selectedOptionContainerClassName="w-full"
                    dropdownClassName="!min-w-[200px] !max-h-[175px] overflow-y-auto no-scrollbar bg-pure-white dark:bg-pure-white-dk"
                    disabled={success || error || disableCategorySelection}
                  />
                  <div className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
                    {(fileItem?.size / (1024 * 1024))?.toFixed(2)} MB
                  </div>
                  {!isLoading && !success && (
                    <button
                      className="w-[14px] h-[14px] bg-verba-indigo dark:bg-verba-indigo-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
                      onClick={() => {
                        setFile(file?.filter((f) => f?.name !== fileItem?.name));
                        setFileSystemNames((prev) => {
                          const updated = new Map(prev);
                          updated?.delete(fileItem?.name);
                          return updated;
                        });
                      }}
                    >
                      <CloseIcon className="w-[5.5px] h-[5.5px] text-pure-white dark:text-pure-white-dk" />
                    </button>
                  )}
                  {isLoading && (
                    <ClipLoader
                      className="w-[4.5px] h-[4.5px] text-verba-blue dark:text-verba-blue-dk"
                      thickness={2}
                      size={14}
                    />
                  )}
                  {success && (
                    <div className="flex items-center justify-center">
                      <CompletedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />{" "}
                    </div>
                  )}
                </div>
              ))}
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 mt-3">
              <ClipLoader
                className="w-[4.5px] h-[4.5px] text-verba-blue dark:text-verba-blue-dk"
                thickness={2}
                size={14}
              />
              <span className="text-xs text-verba-blue dark:text-verba-blue-dk">
                Uploading {file?.length} documents
              </span>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-1 mt-3">
              <CheckIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />{" "}
              <span className="text-xs text-verba-green-success dark:text-verba-green-success-dk font-medium">
                Uploaded
              </span>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-1 mt-3">
              <AlertIcon className="w-[14px] h-[14px] text-verba-red-alert dark:text-verba-red-alert-dk" />
              <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                Failed to upload documents
              </span>
            </div>
          )}
          {!isLoading && !success && !error && (
            <div className="flex gap-2 justify-end items-center mt-3">
              <Button
                type="button"
                innerText="Cancel"
                onClick={() => {
                  setFile([]);
                  setFileSystemNames(new Map());
                }}
                disabled={isLoading}
                buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
              />
              <Button
                type="button"
                onClick={() => handleBulkUpload(file)}
                disabled={
                  isLoading ||
                  file?.length === 0 ||
                  file?.some((f) => !fileSystemNames?.get(f?.name)?.id)
                }
                isLoading={isLoading}
                innerText={`Confirm Upload (${file?.length})`}
                buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white dark:hover:text-pure-white"
                innerTextClass="text-md font-medium whitespace-nowrap"
              />
            </div>
          )}
          {(success || error) && (
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                innerText="Close"
                onClick={() => {
                  setFile([]);
                  setFileSystemNames(new Map());
                  setSuccess(false);
                  setIsLoading(false);
                  setError(false);
                  onClose();
                }}
                disabled={isLoading}
                buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white dark:hover:text-pure-white"
                innerTextClass="text-md font-medium whitespace-nowrap"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadDocuments;
